'use client'

import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'

import { useOpenAddAccountDialog } from '@/hooks/use-open-add-account-dialog'
import { toast } from '@/lib/toast'
import { type ExchangeTradeability, resolveExchangeTradeability } from '@/lib/utils/account-select'
import { useUserAccounts } from '@/service/account.hook'
import { Exchange } from '@/types/generated/common/types'

/**
 * Shared id for the "can't trade this" toasts (here and the watchlist's own), so repeated
 * clicks update one toast instead of stacking a new one each time.
 */
export const UNTRADEABLE_TOAST_ID = 'untradeable-exchange'

type Untradeable = Extract<ExchangeTradeability, { tradeable: false }>

/** Why `exchange` can't take an order — one wording for the toasts and the disabled buttons. */
export function untradeableMessage(result: Untradeable): string {
  return result.reason === 'no-trade-venue'
    ? `${result.label} is available for market data only`
    : `Connect an active ${result.label} account to trade this instrument`
}

/**
 * The same check as `useCanTradeExchange`, as a value for UI that should look disabled up front
 * (the charts' Buy/Sell buttons) rather than toast on click. Tradeable while accounts are still
 * loading, so a button never flashes disabled on a cold load.
 */
export function useExchangeTradeability(exchange: Exchange): ExchangeTradeability {
  const { data } = useUserAccounts()
  const accounts = data?.accounts
  return useMemo(
    () => (accounts ? resolveExchangeTradeability(exchange, accounts) : { tradeable: true }),
    [accounts, exchange]
  )
}

/**
 * Guard for every "add to Order Form" entry point (watchlist Bid/Ask, ticker-tape
 * Buy/Sell, chart plus-click, option-chain Bid/Ask). Returns a stable `canTrade`
 * callback: call it with the row/instrument's exchange before adding a selection —
 * it returns `true` and does nothing when tradeable, or surfaces an explanatory
 * toast and returns `false` when not (no account-creation venue at all — GLBX/
 * XNAS/XNYS/OPRA — or no active, non-read-only account there yet).
 *
 * Deliberately NOT wired into useOrderFormDispatch itself — that would also block
 * removal/restore/internal normalization of an already-selected instrument, which
 * must always be allowed. Call this only at the "add a new selection" call site.
 */
export function useCanTradeExchange() {
  // Read `data.accounts` directly (not `?? []`) as the dependency — react-query keeps
  // the same array reference across re-renders that don't change the data, whereas a
  // `?? []` fallback allocates a fresh array every render and would defeat this
  // callback's memoization.
  const { data } = useUserAccounts()
  const accounts = data?.accounts
  const t = useTranslations('settings')
  const openAddAccountDialog = useOpenAddAccountDialog()

  return useCallback(
    (exchange: Exchange): boolean => {
      const result = resolveExchangeTradeability(exchange, accounts ?? [])
      if (result.tradeable) return true
      if (result.reason === 'no-trade-venue') {
        toast.error(untradeableMessage(result), {
          id: UNTRADEABLE_TOAST_ID,
        })
      } else {
        toast.error(untradeableMessage(result), {
          id: UNTRADEABLE_TOAST_ID,
          action: {
            label: t('addAccount'),
            onClick: openAddAccountDialog,
          },
        })
      }
      return false
    },
    [accounts, t, openAddAccountDialog]
  )
}
